import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import React from "react";

import type { HeroStorytellingBlock } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ButtonPrimary } from "../Clickables";
import { FeaturedRichtext } from "../FeaturedRichtext";
import { H, HLevel } from "../Headings";
import { getThemeClass } from "../ThemedModule";

import styles from "./HeroStorytelling.module.scss";

gsap.registerPlugin(ScrollTrigger);

// How far each card translates upward (in vh) over the full scroll range.
// Larger negative = appears to move faster than the natural document scroll.
const PARALLAX_TRAVEL_VH = [-30, -50, -30];

interface Props {
    value: HeroStorytellingBlock;
}

export const HeroStorytelling = ({ value }: Props) => {
    const rootRef = React.useRef<HTMLElement>(null);
    const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    useGSAP(
        () => {
            if (!rootRef.current) return;
            const mm = gsap.matchMedia();

            mm.add("(prefers-reduced-motion: no-preference)", () => {
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: rootRef.current,
                        start: "top top",
                        end: "bottom top",
                        scrub: true,
                    },
                });

                cardRefs.current.forEach((card, i) => {
                    if (!card) return;
                    tl.fromTo(
                        card,
                        { y: 0 },
                        { y: `${PARALLAX_TRAVEL_VH[i] ?? 0}vh`, ease: "none" },
                        0,
                    );
                });

                return () => tl.kill();
            });

            return () => mm.revert();
        },
        { scope: rootRef },
    );

    return (
        <HLevel>
            <section
                ref={rootRef}
                className={concatClassNames([
                    styles.root,
                    getThemeClass(value.module_theme),
                ])}
            >
                <div className={styles.screenOne}>
                    <div className={styles.textInner}>
                        {value.eyebrow && (
                            <p className={styles.eyebrow}>{value.eyebrow}</p>
                        )}
                        {value.title && (
                            <H className={styles.title}>{value.title}</H>
                        )}
                        {value.body && (
                            <p className={styles.body}>{value.body}</p>
                        )}
                        {value.cta?.display_name && (
                            <ButtonPrimary simpleLink={value.cta} />
                        )}
                    </div>

                    <div className={styles.cards} aria-hidden="true">
                        {[value.image_1, value.image_2, value.image_3].map(
                            (img, i) =>
                                img && (
                                    <div
                                        key={i}
                                        className={
                                            [
                                                styles.cardOne,
                                                styles.cardTwo,
                                                styles.cardThree,
                                            ][i]
                                        }
                                        ref={(el) => {
                                            cardRefs.current[i] = el;
                                        }}
                                    >
                                        <ImageChooserBlock value={img} />
                                    </div>
                                ),
                        )}
                    </div>
                </div>

                <div className={styles.screenTwo}>
                    <div className={styles.textInner}>
                        {value.featured_text && (
                            <FeaturedRichtext value={value.featured_text} />
                        )}
                    </div>
                </div>
            </section>
        </HLevel>
    );
};
